import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Auth } from '@angular/fire/auth';
import { authState } from 'rxfire/auth';
import { switchMap, from, Observable, filter, take, catchError, of, throwError } from 'rxjs';
import { retry, tap } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private auth = inject(Auth);
  private pendingRequests = new Map<string, Observable<HttpEvent<any>>>();

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Crear una clave única para esta petición
    const requestKey = `${req.method}-${req.url}-${JSON.stringify(req.body || {})}`;
    
    // Si ya hay una petición pendiente con la misma clave, retornarla
    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey)!;
    }

    // Crear la petición observable
    const request$ = authState(this.auth).pipe(
      // Filtrar valores null iniciales y esperar a que se resuelva el estado de auth
      filter(user => user !== undefined),
      take(1), // Tomar solo el primer valor válido
      switchMap(user => {
        if (!user) {
          // Si no hay usuario, continuar sin token
          return this.executeRequest(req, next, requestKey);
        }

        return from(user.getIdToken()).pipe(
          switchMap(token => {
            if (!token) {
              // Si no se pudo obtener el token, continuar sin él
              return this.executeRequest(req, next, requestKey);
            }

            const authReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${token}`
              }
            });
            return this.executeRequest(authReq, next, requestKey);
          }),
          catchError(error => {
            console.warn('Error obteniendo token de autenticación:', error);
            // Limpiar la petición pendiente
            this.pendingRequests.delete(requestKey);
            // En caso de error al obtener el token, continuar sin él
            return this.executeRequest(req, next, requestKey);
          })
        );
      }),
      catchError(error => {
        console.warn('Error en el interceptor de autenticación:', error);
        // Limpiar la petición pendiente
        this.pendingRequests.delete(requestKey);
        // En caso de error general, continuar sin token
        return this.executeRequest(req, next, requestKey);
      })
    );

    // Guardar la petición pendiente
    this.pendingRequests.set(requestKey, request$);
    
    return request$;
  }

  private executeRequest(req: HttpRequest<any>, next: HttpHandler, requestKey: string): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      retry(0), // Deshabilitar reintentos
      tap({
        next: () => {
          // Limpiar la petición pendiente cuando se complete exitosamente
          this.pendingRequests.delete(requestKey);
        },
        error: (error: HttpErrorResponse) => {
          // Limpiar la petición pendiente cuando falle
          this.pendingRequests.delete(requestKey);
          // Propagar el error inmediatamente sin reintentos
          throw error;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Limpiar la petición pendiente
        this.pendingRequests.delete(requestKey);
        // Propagar el error inmediatamente
        return throwError(() => error);
      })
    );
  }
}
